import { BASE_URL } from '../sitemap-pages';
import { GOOGLE_REVIEWS } from './google-reviews.generated';
import type { GoogleReview } from './reviews-types';

const GOOGLE_PUBLISHER: Readonly<Record<string, unknown>> = {
  '@type': 'Organization',
  name: 'Google',
  url: 'https://www.google.com/maps',
};

/**
 * Subject of every Google review — Russ's online bass lessons (the Course).
 * Required by Google's Review rich-result spec; without itemReviewed each
 * Review fails Rich Results Test as "missing field". Course is used here
 * rather than EducationalOrganization because Course supports review
 * snippets in search results; self-serving Organization-level reviews are
 * not rich-result eligible. Inlined per-Review (rather than @id-referenced)
 * so each Review object is self-describing for any consumer parsing them
 * individually.
 */
const ITEM_REVIEWED: Readonly<Record<string, unknown>> = {
  '@type': 'Course',
  name: 'Live Online Bass Guitar Lessons',
  description:
    'Live one-on-one bass guitar lessons over Zoom with Russ Rodgers.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'Russ Rodgers Bass Guitar Instruction',
    url: BASE_URL,
  },
};

function reviewToSchema(r: GoogleReview): Record<string, unknown> {
  return {
    '@type': 'Review',
    itemReviewed: ITEM_REVIEWED,
    author: {
      '@type': 'Person',
      name: r.author,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    datePublished: r.date,
    reviewBody: r.text,
    publisher: GOOGLE_PUBLISHER,
    ...(r.url ? { url: r.url } : {}),
  };
}

/**
 * Returns one Review JSON-LD object per Google review with a body.
 * Used in the testimonials route's data.seo.jsonLd alongside the
 * BreadcrumbList. Rating-only reviews are excluded — Google
 * deprioritizes Reviews without a reviewBody — but they still count
 * toward the site-wide aggregateRating that ships in
 * seo.service.ts's EducationalOrganization schema.
 */
export function buildGoogleReviewSchemas(): Record<string, unknown>[] {
  return GOOGLE_REVIEWS.filter((r) => r.text.length > 0).map(reviewToSchema);
}
